import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Target, FileText, Send, BarChart3, Settings2 } from 'lucide-react';
interface SlideGSKProofProps {
  onContinue: () => void;
}
export function SlideGSKProof({
  onContinue
}: SlideGSKProofProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 400);
    const t2 = setTimeout(() => setStage(2), 1200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);
  const engine = [{
    icon: <Target className="w-4 h-4" data-id="element-266" />,
    label: 'Strategy'
  }, {
    icon: <FileText className="w-4 h-4" data-id="element-267" />,
    label: 'Content'
  }, {
    icon: <Send className="w-4 h-4" data-id="element-268" />,
    label: 'Distribution'
  }, {
    icon: <BarChart3 className="w-4 h-4" data-id="element-269" />,
    label: 'Data'
  }, {
    icon: <Settings2 className="w-4 h-4" data-id="element-270" />,
    label: 'Optimization'
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-271">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-272">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-273">
          Proof it works
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-3" data-id="element-274">
          This is not theory. <br data-id="element-275" />
          <span className="text-teal-400" data-id="element-276">We already run this model.</span>
        </h2>
        <p className="text-base text-white/60 max-w-3xl" data-id="element-277">
          In Nigeria and Kenya, Snakker supports GSK Africa with end-to-end HCP
          campaigns: strategy, mobile-first Snaks, quiz-based engagement,
          multi-channel distribution, reporting and localization.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10 items-start" data-id="element-278">
        {/* LEFT — Country cards & Engine */}
        <div className="lg:col-span-6 space-y-6" data-id="element-279">
          <div className="grid grid-cols-2 gap-4" data-id="element-280">
            {['Nigeria', 'Kenya'].map((country, idx) => <motion.div key={country} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: stage >= 1 ? 1 : 0,
            x: stage >= 1 ? 0 : -20
          }} transition={{
            delay: idx * 0.2
          }} className="bg-navy-700/40 border border-teal-400/30 rounded-2xl p-5" data-id="element-281">
                <div className="flex items-center justify-between mb-2" data-id="element-282">
                  <h3 className="text-xl font-serif text-white" data-id="element-283">{country}</h3>
                  <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest text-teal-400 bg-teal-400/10 rounded-full font-medium" data-id="element-284">
                    Live
                  </span>
                </div>
                <p className="text-white/60 text-xs" data-id="element-285">
                  GSK Africa HCP campaigns, end-to-end.
                </p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: stage >= 2 ? 1 : 0
        }} className="bg-navy-800/40 border border-navy-700 rounded-xl p-6 mt-4" data-id="element-286">
            <p className="text-white/40 text-[10px] uppercase tracking-wider mb-4 font-medium text-center" data-id="element-287">
              The Campaign Engine
            </p>
            <div className="flex items-center justify-between relative" data-id="element-288">
              <div className="absolute top-1/2 left-0 w-full h-px bg-teal-400/20 -translate-y-1/2 z-0" data-id="element-289" />
              {engine.map((node, idx) => <div key={node.label} className="relative z-10 flex flex-col items-center gap-2" data-id="element-290">
                  <div className="w-10 h-10 rounded-full bg-navy-900 border border-teal-400/40 flex items-center justify-center text-teal-400 shadow-lg" data-id="element-291">
                    {node.icon}
                  </div>
                  <span className="text-white/60 text-[10px] uppercase tracking-wider" data-id="element-292">
                    {node.label}
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* RIGHT — Roche Africa Context */}
        <div className="lg:col-span-6" data-id="element-293">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: stage >= 2 ? 1 : 0
        }} transition={{
          delay: 0.4
        }} className="bg-teal-400/5 border border-teal-400/20 rounded-2xl p-8 h-full" data-id="element-294">
            <p className="text-teal-300 text-[10px] uppercase tracking-widest font-medium mb-6" data-id="element-295">
              What this means for Roche Africa
            </p>
            <ul className="space-y-5" data-id="element-296">
              {['The same model can support multiple priority brands', 'Campaigns can be adapted country by country', 'HCP engagement becomes measurable', 'Insights from one market can improve the next', 'Arnaud gets results, not execution tasks'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-297">
                  <div className="w-5 h-5 rounded-full bg-teal-400/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-298">
                    <div className="w-2 h-2 rounded-full bg-teal-400" data-id="element-299" />
                  </div>
                  <span className="text-white/90 text-base" data-id="element-300">{item}</span>
                </li>)}
            </ul>

            <div className="mt-8 pt-6 border-t border-teal-400/20" data-id="element-301">
              <p className="text-white/70 text-sm leading-relaxed" data-id="element-302">
                You already have the content. You already have the channels.
                Snakker makes them work together, learn together and improve
                over time.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="flex justify-end mt-6" data-id="element-303">
        <Button onClick={onContinue} data-id="element-304">Build the partnership →</Button>
      </div>
    </div>;
}